import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  COMPLIANCE_TODAY,
  CONSENT_TEXT,
  ENTITY_SCALES,
  GENERIC_FAILURE,
  GENERIC_SUCCESS,
  JURISDICTION_RANGES,
  TIMINGS,
  validatePilotRequest,
  type FieldName,
  type ValidationErrors,
} from "../../content/sentience-pilot";

type State = "idle" | "sending" | "sent" | "failed";

function Field({ name, label, error, children }: { name: FieldName; label: string; error: string | undefined; children: ReactNode }) {
  return (
    <label htmlFor={name}>
      {label}
      {children}
      {error ? <span id={`${name}-error`} className="contact__error" role="alert">{error}</span> : null}
    </label>
  );
}

function described(name: FieldName, errors: ValidationErrors) {
  return errors[name]
    ? { "aria-invalid": true, "aria-describedby": `${name}-error` }
    : { "aria-invalid": false };
}

function Choice({ name, options, errors }: { name: FieldName; options: readonly string[]; errors: ValidationErrors }) {
  return (
    <select id={name} name={name} defaultValue="" {...described(name, errors)}>
      <option value="" disabled>Select…</option>
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  );
}

/**
 * Validation runs here and again in the Function, from the same module. If the
 * endpoint cannot store the request durably the form says so rather than
 * thanking someone for a lead that went nowhere.
 */
export function PilotForm() {
  const [state, setState] = useState<State>("idle");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [failure, setFailure] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const payload = Object.fromEntries(new FormData(event.currentTarget).entries());
    const result = validatePilotRequest(payload);
    if (!result.ok) {
      setErrors(result.errors);
      setState("idle");
      return;
    }
    setErrors({});
    setState("sending");
    try {
      const response = await fetch("/api/pilot-interest", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...result.value, website: payload.website ?? "" }),
      });
      if (!response.ok) {
        setFailure(
          response.status === 503
            ? "Pilot requests are not being accepted from this deployment yet. Please email privacy@odingard.com and we will pick it up."
            : GENERIC_FAILURE,
        );
        setState("failed");
        return;
      }
      setState("sent");
    } catch {
      setFailure(GENERIC_FAILURE);
      setState("failed");
    }
  }

  if (state === "sent") {
    return (
      <div className="sn-sent" role="status">
        <h2>Request received</h2>
        <p>{GENERIC_SUCCESS}</p>
      </div>
    );
  }

  return (
    <form className="contact__form sn-form" onSubmit={submit} noValidate>
      <div className="sn-form__grid">
        <Field name="fullName" label="Full name" error={errors.fullName}>
          <input id="fullName" name="fullName" autoComplete="name" maxLength={120} {...described("fullName", errors)} />
        </Field>
        <Field name="workEmail" label="Work email" error={errors.workEmail}>
          <input id="workEmail" name="workEmail" type="email" autoComplete="email" maxLength={254} {...described("workEmail", errors)} />
        </Field>
        <Field name="organization" label="Organization" error={errors.organization}>
          <input id="organization" name="organization" autoComplete="organization" maxLength={160} {...described("organization", errors)} />
        </Field>
        <Field name="role" label="Role" error={errors.role}>
          <input id="role" name="role" autoComplete="organization-title" maxLength={120} {...described("role", errors)} />
        </Field>
        <Field name="entityScale" label="Legal entities you file for" error={errors.entityScale}>
          <Choice name="entityScale" options={ENTITY_SCALES} errors={errors} />
        </Field>
        <Field name="jurisdictions" label="Jurisdictions to watch" error={errors.jurisdictions}>
          <Choice name="jurisdictions" options={JURISDICTION_RANGES} errors={errors} />
        </Field>
        <Field name="complianceToday" label="How compliance is handled today" error={errors.complianceToday}>
          <Choice name="complianceToday" options={COMPLIANCE_TODAY} errors={errors} />
        </Field>
        <Field name="timing" label="Pilot timing" error={errors.timing}>
          <Choice name="timing" options={TIMINGS} errors={errors} />
        </Field>
      </div>
      <Field name="notes" label="What are you struggling to track? (optional)" error={errors.notes}>
        <textarea id="notes" name="notes" rows={4} maxLength={2000} placeholder="Obligations, jurisdictions or filings you would want the pilot to cover." {...described("notes", errors)} />
      </Field>
      <div className="contact__trap" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" tabIndex={-1} autoComplete="off" />
      </div>
      <label className="sn-form__consent" htmlFor="consent">
        <input id="consent" name="consent" type="checkbox" {...described("consent", errors)} />
        <span>
          {CONSENT_TEXT}
          {errors.consent ? <span id="consent-error" className="contact__error" role="alert">{errors.consent}</span> : null}
        </span>
      </label>
      {state === "failed" ? <p className="contact__error" role="alert">{failure}</p> : null}
      <button className="btn btn--primary" type="submit" disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : "Request a Pilot"}
      </button>
      <p className="sn-note">
        We use what you send only to respond about the pilot. Nothing is sold or shared. See the <Link to="/sentience/privacy">Sentience privacy notice</Link> and <Link to="/sentience/terms">terms</Link>.
      </p>
    </form>
  );
}
